import React from "react";
import type {
  TagFilter,
  MetadataKey,
  MetadataValue,
  CriteriaFilter,
  ScoreFilter,
} from "../types";
import type { DateWindow } from "../utils/dateWindow";
import { windowLabel } from "../utils/dateWindow";
import { useFilterPanel } from "../hooks/useFilterPanel";
import { useCriteriaFilter } from "../hooks/useCriteriaFilter";
import { useDateRangeFilter } from "../hooks/useDateRangeFilter";
import { SearchableCriteriaDropdown } from "./SearchableCriteriaDropdown";
import { SearchableTagDropdown } from "./SearchableTagDropdown";
import { DateRangeFilter as DateRangeFilterComponent } from "./DateRangeFilter";
import { ScoreFilter as ScoreFilterComponent, formatScoreRangeLabel } from "./ScoreFilter";
import { useScoreFilter } from "../hooks/useScoreFilter";
import { CheckCircleIcon, TagIcon } from "@heroicons/react/20/solid";
import { FilterChip } from "../../../ui";
import { formatTagValue } from "../../tag-aliases/formatTagValue";
import { useTagAliases } from "../../tag-aliases/context";
import { CopyLinkButton } from "./CopyLinkButton";

interface FilterPanelProps {
  metadataKeys: MetadataKey[];
  filters: TagFilter[];
  onAddFilter: (filter: TagFilter) => void;
  onRemoveFilter: (key: string) => void;
  onUpdateFilter: (key: string, filter: Partial<TagFilter>) => void;
  metadataValues?: Record<string, MetadataValue[]>;
  loadMetadataValuesForKey?: (key: string) => Promise<void>;
  criteriaFilter?: CriteriaFilter;
  criteria?: string[];
  loadingCriteria?: boolean;
  onCriteriaFilterChange?: (filter?: CriteriaFilter) => void;
  onLoadCriteria?: () => Promise<void>;
  maxCriteriaDisplay?: number;
  dateWindow?: DateWindow;
  onDateWindowChange?: (window?: DateWindow) => void;
  scoreFilter?: ScoreFilter;
  onScoreFilterChange?: (filter?: ScoreFilter) => void;
}

/**
 * Container component for the filter panel.
 * Uses custom hook for logic and composes presentational components.
 */
export function FilterPanel({
  metadataKeys,
  filters,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  metadataValues = {},
  loadMetadataValuesForKey,
  criteriaFilter,
  criteria = [],
  loadingCriteria = false,
  onCriteriaFilterChange,
  onLoadCriteria,
  maxCriteriaDisplay = 20,
  dateWindow,
  onDateWindowChange,
  scoreFilter,
  onScoreFilterChange,
}: FilterPanelProps) {
  const {
    expandedKey,
    selectedValues,
    handleToggleKey,
    handleSelectValue,
    handleApplyFilter,
    handleCancelFilter,
    handleRemoveFilter,
  } = useFilterPanel({
    filters,
    loadMetadataValuesForKey,
    onAddFilter,
    onRemoveFilter,
    onUpdateFilter,
  });

  // Use custom hook for criteria filter logic
  const criteriaFilterState = useCriteriaFilter({
    criteriaFilter,
    onCriteriaFilterChange,
    onLoadCriteria,
    maxCriteriaDisplay,
  });

  // Use custom hook for date range filter logic
  const dateRangeFilterState = useDateRangeFilter({
    dateWindow,
    onDateWindowChange,
  });

  // Use custom hook for score filter logic
  const scoreFilterState = useScoreFilter({
    scoreFilter,
    onScoreFilterChange,
  });

  // Chips read the display name; the filter itself still holds the raw value.
  const { aliases } = useTagAliases();

  const containerBaseClasses =
    "bg-card rounded-lg shadow-sm border border-border p-5";

  const titleBaseClasses = "font-semibold text-base mb-5 text-foreground";

  const buttonsContainerClasses = "flex flex-wrap gap-2 mb-4";

  // Check if we have any filters to show
  const hasDateFilter = !!onDateWindowChange;
  const hasCriteriaFilter = !!onCriteriaFilterChange;
  const hasScoreFilter = !!onScoreFilterChange;
  const hasTagFilters = metadataKeys.length > 0;

  // Check if any filters are active for the active filters row
  const hasActiveCriteriaFilter = criteriaFilter && criteriaFilter.values.length > 0;

  return (
    <div className={containerBaseClasses}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className={titleBaseClasses}>Filters</h3>
        <CopyLinkButton />
      </div>

      <div className={buttonsContainerClasses}>
        {/* Date Range Filter */}
        {hasDateFilter && (
          <>
            <DateRangeFilterComponent
              ref={dateRangeFilterState.containerRef}
              isExpanded={dateRangeFilterState.isExpanded}
              onToggle={() => dateRangeFilterState.setIsExpanded(!dateRangeFilterState.isExpanded)}
              selectedPreset={dateRangeFilterState.selectedPreset}
              customStartDate={dateRangeFilterState.customStartDate}
              customEndDate={dateRangeFilterState.customEndDate}
              onPresetSelect={dateRangeFilterState.handlePresetSelect}
              onStartDateChange={dateRangeFilterState.setCustomStartDate}
              onEndDateChange={dateRangeFilterState.setCustomEndDate}
              onApply={dateRangeFilterState.handleApply}
              onCancel={dateRangeFilterState.handleCancel}
              onRemove={dateRangeFilterState.handleRemove}
              isActive={dateRangeFilterState.isActive}
              canApply={dateRangeFilterState.canApply}
              currentLabel={windowLabel(dateWindow)}
            />
            {(hasScoreFilter || hasCriteriaFilter || hasTagFilters) && (
              <div className="h-6 w-px bg-border mx-1" />
            )}
          </>
        )}

        {/* Score Filter */}
        {hasScoreFilter && (
          <>
            <ScoreFilterComponent
              ref={scoreFilterState.containerRef}
              isExpanded={scoreFilterState.isExpanded}
              onToggle={() => scoreFilterState.setIsExpanded(!scoreFilterState.isExpanded)}
              minScore={scoreFilterState.minScore}
              maxScore={scoreFilterState.maxScore}
              onSliderChange={scoreFilterState.handleSliderChange}
              onApply={scoreFilterState.handleApply}
              onCancel={scoreFilterState.handleCancel}
              onRemove={scoreFilterState.handleRemove}
              isActive={scoreFilterState.isActive}
              canApply={scoreFilterState.canApply}
              currentLabel={formatScoreRangeLabel(scoreFilter)}
              sliderMin={scoreFilterState.sliderMin}
              sliderMax={scoreFilterState.sliderMax}
              sliderStep={scoreFilterState.sliderStep}
            />
            {(hasCriteriaFilter || hasTagFilters) && (
              <div className="h-6 w-px bg-border mx-1" />
            )}
          </>
        )}

        {/* Criteria Filter */}
        {hasCriteriaFilter && (
          <>
            <SearchableCriteriaDropdown
              ref={criteriaFilterState.containerRef}
              isExpanded={criteriaFilterState.isExpanded}
              onToggle={() => criteriaFilterState.setIsExpanded(!criteriaFilterState.isExpanded)}
              values={criteria}
              selectedValues={criteriaFilterState.selectedValues}
              isLoading={loadingCriteria}
              isActive={criteriaFilterState.isActive}
              onSelectValue={criteriaFilterState.handleValueSelect}
              onApply={criteriaFilterState.handleApply}
              onCancel={criteriaFilterState.handleCancel}
              onRemove={criteriaFilterState.handleRemove}
              canApply={criteriaFilterState.canApply}
            />
            {hasTagFilters && (
              <div className="h-6 w-px bg-border mx-1" />
            )}
          </>
        )}

        {/* Tag/Label Filters */}
        {metadataKeys.map((keyData) => {
          const filter = filters.find((f) => f.key === keyData.key);
          const isActive = !!filter;
          const isExpanded = expandedKey === keyData.key;
          const values = metadataValues[keyData.key] || [];
          const isLoading =
            loadMetadataValuesForKey !== undefined && !metadataValues[keyData.key];
          const canApply = (selectedValues[keyData.key] || new Set()).size > 0;

          return (
            <SearchableTagDropdown
              key={keyData.key}
              tagKey={keyData.key}
              filter={filter}
              isExpanded={isExpanded}
              values={values}
              isLoading={isLoading}
              canApply={canApply}
              selectedValues={selectedValues[keyData.key] || new Set()}
              onToggle={() => handleToggleKey(keyData.key)}
              onRemove={isActive ? () => handleRemoveFilter(keyData.key) : undefined}
              onSelectValue={(value) => handleSelectValue(keyData.key, value)}
              onApply={() => handleApplyFilter(keyData.key)}
              onCancel={() => handleCancelFilter(keyData.key)}
            />
          );
        })}
      </div>

      {/* Active Filters Row — only criteria/label chips. The date & score ranges are
          already displayed on their own control pills, so showing them again here was
          redundant (016 §5 — de-duplicated date filter). One unified chip style. */}
      {(hasActiveCriteriaFilter || hasTagFilters) && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
          {/* Criteria Filter Chips */}
          {hasActiveCriteriaFilter && criteriaFilter!.values.map((criteria) => (
            <FilterChip
              key={criteria}
                icon={<CheckCircleIcon className="h-3 w-3" />}
                onRemove={() => {
                  const newValues = criteriaFilter!.values.filter(v => v !== criteria);
                  if (newValues.length === 0) {
                    onCriteriaFilterChange?.(undefined);
                  } else {
                    onCriteriaFilterChange?.({ values: newValues });
                  }
                }}
              >
                <span className="font-medium">{criteria}</span>
              </FilterChip>
          ))}

          {/* Label Filters Chips */}
          {filters.map((filter) => (
            <FilterChip
              key={filter.key}
              icon={<TagIcon className="h-3 w-3" />}
              onRemove={() => onRemoveFilter(filter.key)}
            >
              <span className="font-medium">{filter.key}:</span>
              <span>
                {filter.values.map((v) => formatTagValue(v, aliases)).join(", ")}
              </span>
            </FilterChip>
          ))}
        </div>
      )}
    </div>
  );
}
